import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import Card from 'reactstrap/lib/Card';
import Button from 'reactstrap/lib/Button';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildPopularStylesPath, VehicleModel } from 'client/data/models/vehicle-v2';
import { getMediaImageUrl, getS3MediaProfileImageUrlBySize } from 'client/utils/image-helpers';
import { BuildPriceEntities, BuildPriceModel, BuildPricePaths } from 'client/data/models/build-price';
import { SUBMODEL_IMAGE_SIZE } from 'site-modules/shared/constants/build-price';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { getColorPhoto, getVehicleSummarySections } from 'site-modules/shared/utils/build-price/build-price';
import { PhotoflipperButtonAsync } from 'site-modules/shared/components/photoflipper-core/photoflipper-button/photoflipper-button-async';
import { UnorderedList } from 'site-modules/shared/components/unordered-list/unordered-list';
import { Link } from 'site-modules/shared/components/link/link';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';

import './build-price-vehicle-summary.scss';

export function BuildPriceVehicleSummaryUI({
  buildPriceFilter,
  styleDetails = null,
  modelYear: { makeName, modelName, makeSlug, modelSlug, year, submodels } = null,
  optionsConfig = null,
  optionsList = null,
  colorPhotos = null,
}) {
  const [selectedColorPhoto, setSelectedColorPhoto] = useState(null);

  const sortedOptionIds = buildPriceFilter.optionIds.split(',').sort().join(',');
  const colorPhoto = useMemo(
    () => getColorPhoto({ colorPhotos, optionsList, optionIds: sortedOptionIds }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortedOptionIds]
  );

  useEffect(() => {
    if (colorPhoto) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- retain the last valid image while photo data reloads.
      setSelectedColorPhoto(colorPhoto);
    }
  }, [colorPhoto]);

  const currentColorPhoto = selectedColorPhoto || colorPhoto;

  const submodelName = get(buildPriceFilter, 'styleFilter.submodel', '');
  const summarySections = getVehicleSummarySections({
    year,
    buildPriceFilter,
    styleName: get(styleDetails, 'name'),
    optionsConfig,
    optionsList,
  });

  return (
    <Card className="build-price-vehicle-summary p-1" style={{ border: 'none' }}>
      <h2 className="heading-4 text-center mb-1">
        Your {year} {makeName} {modelName} Build
      </h2>
      <PhotoflipperButtonAsync
        makeSlug={makeSlug}
        modelSlug={modelSlug}
        year={year}
        submodel={submodelName ? `${modelName} ${submodelName}` : undefined}
      >
        <Button
          tag="div"
          role="button"
          color="link"
          size="lg"
          className="photoflipper-link-btn p-0 text-primary-darker text-transform-none border-0 w-100"
          aria-label={`See ${year} ${makeName} ${modelName} ${submodelName} photos`}
        >
          <AspectRatioContainer aspectRatio="3:2">
            {currentColorPhoto ? (
              <img className="w-100" src={getMediaImageUrl(currentColorPhoto.src)} alt="" />
            ) : (
              <img
                className="w-100"
                src={getS3MediaProfileImageUrlBySize(
                  {
                    year,
                    make: { slug: makeSlug },
                    model: { slug: modelSlug },
                    submodels: { slug: makeNiceName(submodelName) || get(submodels, '[0].slug') },
                  },
                  SUBMODEL_IMAGE_SIZE.SUMMARY.WIDTH,
                  SUBMODEL_IMAGE_SIZE.SUMMARY.HEIGHT
                )}
                alt=""
              />
            )}
          </AspectRatioContainer>
        </Button>
      </PhotoflipperButtonAsync>

      {!!get(summarySections, 'length') && (
        <ul className="summary-list list-unstyled mb-0 mt-0_75">
          {summarySections.map(({ name, value, items, changeLink }) => (
            <li key={name} className="d-flex align-items-center justify-content-between py-0_75 medium">
              <div className="pe-1 text-gray-darker">
                <h3 className="heading-6 mb-0">{name}</h3>
                {!!value && <div>{value}</div>}
                {!!get(items, 'length') && <UnorderedList items={items} isUnstyled />}
              </div>
              <div>
                <Link
                  to={changeLink.url}
                  data-tracking-id={changeLink.trackingId}
                  className="text-primary-darker text-underline"
                >
                  Change
                  <span className="visually-hidden">&nbsp;{name}</span>
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

BuildPriceVehicleSummaryUI.propTypes = {
  buildPriceFilter: BuildPriceEntities.BuildPriceFilter.isRequired,
  styleDetails: PropTypes.shape({ name: PropTypes.string }),
  modelYear: BuildPriceEntities.ModelYear,
  optionsConfig: BuildPriceEntities.OptionsConfig,
  optionsList: PropTypes.arrayOf(BuildPriceEntities.Option),
  colorPhotos: PropTypes.arrayOf(BuildPriceEntities.ColorPhoto),
};

const stateToPropsConfig = {
  styleDetails: bindToPath(
    ({ modelYear: { makeSlug, modelSlug, year }, buildPriceFilter: { styleId } }) =>
      styleId && buildPopularStylesPath({ make: makeSlug, model: modelSlug, year }),
    VehicleModel,
    (styles, { buildPriceFilter: { styleId } }) => styles.find(({ id }) => id === parseInt(styleId, 10))
  ),
  // used starting from STEP 4
  optionsConfig: bindToPath(
    ({ buildPriceFilter: { styleId, optionIds } }) =>
      !!styleId && BuildPricePaths.buildConfigurationWithOptionsPath(styleId, optionIds),
    BuildPriceModel
  ),
  optionsList: bindToPath(
    ({ buildPriceFilter: { styleId } }) => !!styleId && BuildPricePaths.buildStyleOptionsPath(styleId),
    BuildPriceModel
  ),
  colorPhotos: bindToPath(
    ({ modelYear: { makeSlug, modelSlug, year, modelName }, buildPriceFilter: { styleFilter, styleId } }) =>
      !!styleId &&
      BuildPricePaths.buildColorPhotosPath({
        make: makeSlug,
        model: modelSlug,
        year,
        submodel: `${modelName} ${styleFilter.submodel}`,
      }),
    BuildPriceModel
  ),
};

export const BuildPriceVehicleSummary = connectToModel(BuildPriceVehicleSummaryUI, stateToPropsConfig);
